<template>
  <div class="flex h-full flex-col gap-5">
    <div class="flex flex-shrink-0 flex-row gap-4">
      <FontAwesomeIcon :icon="['fal', 'database']" size="2x"></FontAwesomeIcon>
      <h2 class="capitalize-first flex-shrink-0 text-xl font-medium">
        {{ $t('attributes.addresses') + ' ' + $t('global.wiki').toUpperCase() }}
      </h2>
      <div class="flex w-full justify-end gap-x-1">
        <BaseButton
          :title="
            addWikiPlaceTab
              ? $t('button.back_to_list')
              : $t('button.add_wiki_place')
          "
          :icon="addWikiPlaceTab ? 'long-arrow-left' : 'plus'"
          :color="addWikiPlaceTab ? 'dark' : 'warning'"
          size="extra-small"
          inverse-icon-order
          :disabled="
            !newWikiPlace.type ||
            !['HOP', 'MDR', 'CIM', 'CRE', 'EGL', 'WREC'].includes(
              newWikiPlace.type
            )
          "
          @click="toggleAddingWikiPlaceTab"
        />
      </div>
    </div>
    <template v-if="addWikiPlaceTab">
      <div class="flex flex-col gap-y-2 overflow-auto">
        <SelectComponent
          v-model="newWikiPlace.type"
          :label="$t('attributes.place_type')"
          name="type"
          :values="initialMetadata?.enums?.place_types"
          track-by="id"
          display-key="name"
          disabled
        />
        <SelectCity
          v-model:city-id="newWikiPlace.city_id"
          v-model:city="newWikiPlace.city"
          v-model:zip="newWikiPlace.zip"
          v-model:city-object="newWikiPlace.city_object"
          :edition-mode="true"
          :required="true"
        />
        <BaseShowEditTranslation
          v-model:translations="newWikiPlace.name"
          :label="$t('attributes.name')"
          name="name"
          :edition-mode="true"
          :required="true"
        />
        <BaseShowEditTextArea
          v-model="newWikiPlace.remarks"
          name="acces_difficile"
          :label="$t('attributes.remarks')"
          :edition-mode="true"
        />
        <BaseShowEditPhoneInput
          v-model:country-value="newWikiPlace.phone_country"
          v-model:number-value="newWikiPlace.phone_field"
          :label="$t('attributes.phone')"
          :edition-mode="true"
          name="phone"
          :metadata="initialMetadata?.enums"
        />

        <BaseShowEditPhoneInput
          v-model:country-value="newWikiPlace.fax_country"
          v-model:number-value="newWikiPlace.fax_field"
          :label="$t('attributes.fax')"
          :edition-mode="true"
          name="fax"
          :metadata="initialMetadata?.enums"
        />
        <BaseShowEditInput
          v-model="newWikiPlace.web"
          :label="$t('attributes.web')"
          :edition-mode="true"
          name="web"
        />
        <BaseShowEditInput
          v-model="newWikiPlace.mail"
          :label="$t('attributes.email')"
          :edition-mode="true"
          name="mail"
        />
        <div class="my-2 flex justify-end">
          <BaseButton
            :title="
              newWikiPlace.address_id
                ? $t('global.update_address')
                : $t('button.add_address')
            "
            :icon="newWikiPlace.address_id ? 'pen' : 'plus'"
            :disabled="!newWikiPlace.city_id"
            @click="isAddressModalOpen = true"
          />
        </div>
        <div v-if="newWikiPlace.address_id">
          <BaseShowEditAddress
            :country="newWikiPlace.address.country"
            :city-id="newWikiPlace.address.city_id"
            :zip="newWikiPlace.address.zip"
            :city="newWikiPlace.address.city"
            :current-city-object="{
              id: newWikiPlace.address.city_id,
              full_name: `${newWikiPlace.address.zip} ${newWikiPlace.address.city}`
            }"
            :extra-street="newWikiPlace.address.extra_street"
            :street="newWikiPlace.address.street"
            :edition-mode="false"
            name="address_id"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="grid flex-shrink-0 grid-cols-3 items-end gap-2">
        <div class="col-span-1">
          <SelectSimpleComponent
            v-model="searchType"
            :options="listPlaceType"
            :label="$t('attributes.type')"
          />
        </div>
        <div class="col-span-2">
          <BaseSearchInput v-model="keyword" />
        </div>
      </div>
      <template v-if="loading">
        <LoadingComponent />
      </template>
      <div v-else class="flex-1 overflow-y-auto">
        <div
          v-if="children && children.length"
          class="border-gray-300 pb-4 dark:border-gray-700"
        >
          <h3
            class="capitalize-first mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100"
          >
            {{ $t('global.linked_dossier_rooms') }}
          </h3>

          <div class="mb-4 grid flex-1 grid-cols-2 gap-3">
            <div
              v-for="item in localChildren"
              :key="item.id"
              class="relative cursor-pointer"
              @click="handelSelectWiki(item)"
            >
              <span
                class="SelectWikiPlace absolute right-0 bottom-0 rounded-tl-md rounded-br-md border bg-white px-2 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-slate-700 dark:text-gray-100"
              >
                {{ item.distance_in_kilometers }} km
              </span>

              <BaseCard
                title="title"
                :entry="item"
                :selected="placeId === item.id && placeType === searchType"
              >
                <template #card-details="{ entry: wikiPlace }">
                  <div
                    class="flex flex-col text-sm text-gray-700 dark:text-gray-200"
                  >
                    <div v-if="wikiPlace.street">{{ wikiPlace.street }}</div>
                    <div v-if="wikiPlace.zip">
                      {{ wikiPlace.zip }} {{ wikiPlace.city }}
                    </div>
                  </div>
                </template>
              </BaseCard>
            </div>
          </div>
        </div>
        <div
          v-if="items && items.length"
          class="grid flex-1 grid-cols-2 gap-2 overflow-auto"
        >
          <div
            v-for="item in localItems"
            :key="item.id"
            class="relative"
            @click="handelSelectWiki(item)"
          >
            <span
              class="SelectWikiPlace absolute right-0 bottom-0 rounded-tl-md rounded-br-md border bg-white px-2 py-1 dark:border dark:border-white dark:bg-slate-700"
            >
              {{ item.distance_in_kilometers }} km
            </span>
            <BaseCard
              title="title"
              :entry="item"
              :selected="placeId === item.id && placeType === searchType"
            >
              <template #card-details="{ entry: wikiPlace }">
                <div class="flex flex-col">
                  <div v-if="wikiPlace.street">
                    {{ wikiPlace.street }}
                  </div>
                  <div v-if="wikiPlace.zip">
                    {{ wikiPlace.zip }} {{ wikiPlace.city }}
                  </div>
                </div>
              </template>
            </BaseCard>
          </div>
        </div>
        <div
          v-else-if="items && Array.isArray(items)"
          class="flex justify-center text-2xl font-medium"
        >
          {{ $t('global.no_search_result') }}
        </div>
      </div>
    </template>
  </div>
  <AddressModal
    v-model="newWikiPlace.address_id"
    v-model:open="isAddressModalOpen"
    :wiki-address="true"
    :place-city-id="newWikiPlace.city_id || newWikiPlace?.id"
    :prefill="newAddressPrefill"
    @change="handleAddressModalChange"
  />
</template>

<script>
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import { debounce } from '@u/debounce'
import { mapActions, mapGetters } from 'vuex'
import SelectSimpleComponent from '@c/BaseFormComponent/SelectSimpleComponent.vue'
import { PlaceType } from '@/assets/enums'
import BaseCard from '@c/BaseCard.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import SelectCity from '@c/SelectAjaxComponent/SelectCity.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditTranslation from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTranslation.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditPhoneInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditPhoneInput.vue'
import AddressModal from '@ui/modal/address/AddressModal.vue'
import BaseShowEditAddress from '@c/BaseShowEditAddress.vue'

export default {
  name: 'SelectWikiPlace',
  components: {
    BaseShowEditAddress,
    AddressModal,
    BaseShowEditPhoneInput,
    BaseShowEditInput,
    BaseShowEditTextArea,
    BaseShowEditTranslation,
    SelectComponent,
    SelectCity,
    BaseButton,
    FontAwesomeIcon,
    LoadingComponent,
    SelectSimpleComponent,
    BaseSearchInput,
    BaseCard
  },
  props: {
    placeModel: {
      type: Object,
      required: true
    },
    placeType: {
      type: String,
      required: true
    },
    placeId: {
      type: [String, Number],
      required: true
    },
    availablePlaceType: {
      type: Array,
      required: true
    },
    defaultPlaceGroup: {
      type: String,
      required: false,
      default: null
    },
    defaultPlaceType: {
      type: String,
      required: false,
      default: null
    },
    latitude: {
      type: Number,
      required: false,
      default: null
    },
    longitude: {
      type: Number,
      required: false,
      default: null
    },
    addWikiPlaceTab: {
      type: Boolean,
      required: false,
      default: false
    },
    newWikiPlace: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: [
    'update:placeType',
    'update:placeId',
    'update:addWikiPlaceTab',
    'update:placeName',
    'close',
    'update:placeModel'
  ],
  data() {
    return {
      keyword: '',
      searchType: null,
      items: null,
      children: [],
      loading: false,
      isAddressModalOpen: false
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    localItems() {
      const selected = this.items.find((it) => it.id === this.placeId)

      if (selected) {
        return [selected, ...this.items.filter((it) => it !== selected)]
      }

      return this.items
    },

    localChildren() {
      const selected = this.children.find((it) => it.id === this.placeId)

      if (selected) {
        return [selected, ...this.children.filter((it) => it !== selected)]
      }

      return this.children
    },
    listPlaceType() {
      return this.filteredPlaceType.map((item) => {
        return { id: item, name: this.$t('attributes.' + item) }
      })
    },
    // What the user already typed on this tab, handed to the address modal so
    // the city, its country and the name are not asked a second time.
    newAddressPrefill() {
      const city = this.newWikiPlace?.city_object

      return {
        name: this.newWikiPlace?.name?.[this.$i18n.locale] ?? null,
        city_id: this.newWikiPlace?.city_id ?? null,
        city: this.newWikiPlace?.city ?? city?.city ?? null,
        zip: this.newWikiPlace?.zip ?? city?.zip ?? null,
        country: city?.country ?? null
      }
    },
    filteredPlaceType() {
      return [
        PlaceType.CREMATORIUM,
        PlaceType.SALLE_CREMATORIUM,
        PlaceType.CIMETIERE,
        PlaceType.LIEU_CEREMONIE,
        PlaceType.EGLISE,
        PlaceType.ADMINISTRATION_COMMUNALE,
        PlaceType.HOPITAL,
        PlaceType.MAISON_DE_REPOS,
        PlaceType.WIKI_FUNERARIUM,
        PlaceType.WIKI_SALLE_CEREMONIE,
        PlaceType.WIKI_SALLE_DE_RECEPTION
      ].filter((item) => this.availablePlaceType.includes(item))
    }
  },
  watch: {
    keyword(_newValue, _oldValue) {
      this.debouncedSearch(this.searchType, this.keyword)
    },
    searchType(_newValue, _oldValue) {
      this.debouncedSearch(this.searchType, this.keyword)
      this.newWikiPlace.type = this.searchType
    },
    newWikiPlace: {
      handler(newValue) {
        this.$emit('update:new-wiki-place', newValue)
      },
      deep: true
    }
  },
  created() {
    this.debouncedSearch = debounce((type, terms) => {
      this.performSearch(type, terms)
    }, 1000)
  },
  mounted() {
    if (this.filteredPlaceType.includes(this.placeType)) {
      this.searchType = this.placeType
    } else {
      this.searchType = this.defaultPlaceType ?? this.filteredPlaceType[0]
    }
  },
  methods: {
    ...mapActions({
      processFetchWikiPlaceByType: 'wiki/fetchWikiPlaceByType',
      processFetchWikiPlaceWithRoomsByTypeAction:
        'wiki/fetchWikiPlaceWithRoomsByType'
    }),
    performSearch(type, terms) {
      let placeIds = []

      if (this.availablePlaceType.includes(PlaceType.WIKI_SALLE_CEREMONIE)) {
        placeIds = [
          ...new Set(
            this.dossier?.events
              ?.filter((event) =>
                [
                  PlaceType.HOPITAL,
                  PlaceType.MAISON_DE_REPOS,
                  PlaceType.CIMETIERE,
                  PlaceType.CREMATORIUM,
                  PlaceType.SALLE_CREMATORIUM,
                  PlaceType.ADMINISTRATION_COMMUNALE,
                  PlaceType.EGLISE,
                  PlaceType.WIKI_FUNERARIUM
                ].includes(event.type)
              )
              .map((event) => event.place_id) ?? []
          )
        ]
      }

      if ((terms && terms.length >= 3) || (this.latitude && this.longitude)) {
        this.loading = true
        const payload = {
          terms: terms || '',
          type: type,
          latitude: this.latitude,
          longitude: this.longitude,
          place_ids: placeIds
        }

        this.processFetchWikiPlaceWithRoomsByTypeAction(payload).then(
          (response) => {
            this.items = response.places
            this.children = response.children_places
            this.loading = false
          }
        )
      }
    },
    // Called by the parent right after a wiki place is created, so the list
    // shows it immediately instead of looking as if nothing was saved.
    refreshSearch() {
      this.performSearch(this.searchType, this.keyword)
    },
    handelSelectWiki(item) {
      this.$emit('update:placeType', this.searchType)

      if (this.placeId === item.id) {
        this.$emit('update:placeId', null)
        this.$emit('update:placeModel', {})
      } else {
        this.$emit('update:placeId', item.id)
        this.$emit(
          'update:placeModel',
          item.wiki_place_resource
            ? {
                ...item.wiki_place_resource,
                title: item.title ?? item.name,
                ...(item.full_name !== undefined && { full_name: item.full_name })
              }
            : {
                name: item.title ?? item.name,
                city: item.city,
                country: item.country,
                street: item.street,
                zip: item.zip,
                cremagest: item.cremagest
              }
        )
      }

      this.$emit('close', true)
    },
    toggleAddingWikiPlaceTab() {
      this.$emit('update:addWikiPlaceTab', !this.addWikiPlaceTab)
    },
    handleAddressModalChange(address) {
      this.newWikiPlace.address = address
    }
  }
}
</script>

<style scoped></style>
